import api from './api';

export type ExecutionTier = 'local' | 'pyodide' | 'lambda_user' | 'lambda_generic';

export interface EvalFunctionConfigProperty {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
  description?: string;
  default?: unknown;
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
  items?: EvalFunctionConfigProperty;
}

export interface EvalFunctionConfigSchema {
  type: 'object';
  properties?: Record<string, EvalFunctionConfigProperty>;
  required?: string[];
}

export interface EvalFunction {
  id: string;
  name: string;
  description?: string;
  tier: ExecutionTier;
  code?: string;
  lambda_arn?: string;
  lambda_region?: string;
  timeout_ms: number;
  returns_ground_truth: boolean;
  is_builtin: boolean;
  config_schema?: EvalFunctionConfigSchema;
  created_at?: string;
  updated_at?: string;
}

export interface CreateFunctionRequest {
  name: string;
  description?: string;
  tier: ExecutionTier;
  code?: string;
  lambda_arn?: string;
  lambda_region?: string;
  timeout_ms?: number;
  returns_ground_truth?: boolean;
  config_schema?: EvalFunctionConfigSchema;
}

export interface UpdateFunctionRequest {
  name?: string;
  description?: string;
  tier?: ExecutionTier;
  code?: string;
  lambda_arn?: string;
  lambda_region?: string;
  timeout_ms?: number;
  returns_ground_truth?: boolean;
  config_schema?: EvalFunctionConfigSchema;
}

export interface TestRequest {
  input_data: Record<string, any>;
  output_data: Record<string, any>;
  expected?: Record<string, any>;
  config?: Record<string, any>;
}

export interface TestResult {
  passed: boolean;
  score: number;
  ground_truth?: any;
  details?: Record<string, any>;
  error?: string;
  execution_time_ms?: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

export interface ListFunctionsResponse {
  functions: EvalFunction[];
}

export const evalFunctionApi = {
  /**
   * List all eval functions (builtin + user-defined)
   */
  async list(tier?: ExecutionTier): Promise<EvalFunction[]> {
    const params = tier ? { tier } : {};
    const response = await api.get<ListFunctionsResponse>('/evals/functions', { params });
    return response.data.functions;
  },

  /**
   * Get a single eval function by ID
   */
  async get(id: string): Promise<EvalFunction> {
    const response = await api.get<EvalFunction>(`/evals/functions/${id}`);
    return response.data;
  },

  /**
   * Create a new user-defined eval function
   */
  async create(data: CreateFunctionRequest): Promise<EvalFunction> {
    const response = await api.post<EvalFunction>('/evals/functions', data);
    return response.data;
  },

  /**
   * Update an existing user-defined eval function
   */
  async update(id: string, data: UpdateFunctionRequest): Promise<EvalFunction> {
    const response = await api.put<EvalFunction>(`/evals/functions/${id}`, data);
    return response.data;
  },

  /**
   * Delete a user-defined eval function
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/evals/functions/${id}`);
  },

  /**
   * Test an eval function with sample data
   */
  async test(id: string, testData: TestRequest): Promise<TestResult> {
    const response = await api.post<TestResult>(`/evals/functions/${id}/test`, testData);
    return response.data;
  },

  /**
   * Test code directly without creating a function first
   */
  async testCode(code: string, tier: ExecutionTier, testData: TestRequest): Promise<TestResult> {
    const response = await api.post<TestResult>('/evals/functions/test-code', {
      code,
      tier,
      ...testData,
    });
    return response.data;
  },

  /**
   * Validate function code without executing it
   */
  async validate(code: string, tier: ExecutionTier): Promise<ValidationResult> {
    const response = await api.post<ValidationResult>('/evals/functions/validate', {
      code,
      tier,
    });
    return response.data;
  },
};

export default evalFunctionApi;
